import { Injectable, inject } from '@angular/core';
import { INavContent } from '@garmin/components-global-web';
import { ISubCategory } from '@garmin/components-global-web/dist/types/components/vertical-nav/interfaces';
import { Scope } from '@garmin-avcloud/avcloud-fly-web-common/admin-authorization';
import { Observable, defaultIfEmpty, forkJoin, map, of } from 'rxjs';
import { AuthINavContent, AuthISubcategory } from '../../../modules/app-routing-structure';
import { FeatureFlagService } from '../feature-flag/feature-flag.service';
import { LdapTokenService } from '../ldap-token/ldap-token.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationTreeService {
  private readonly ldapTokenService = inject(LdapTokenService);
  private readonly featureFlagService = inject(FeatureFlagService);

  buildAuthorizedNavigationTree(navigationTree: AuthINavContent[]): Observable<INavContent[]> {
    const authorizedNavContentObservables = navigationTree.map((navContent) =>
      this.buildAuthorizedNavContent(navContent)
    );
    return forkJoin(authorizedNavContentObservables).pipe(
      map((authNavContent: Array<INavContent | null>) => {
        return authNavContent.reduce((tree, authNav) => {
          if (authNav != null) {
            tree.push({ ...authNav, id: tree.length });
          }
          return tree;
        }, [] as INavContent[]);
      })
    );
  }

  private buildAuthorizedNavContent(nav: AuthINavContent): Observable<INavContent | null> {
    return forkJoin({
      navFeatureFlagEnabled:
        nav.featureFlag != null ? this.featureFlagService.isFeatureActive(nav.featureFlag) : of(true),
      navScopesEnabled: this.verifyRequiredScopes(nav.scopes, nav.requireAllScopes),
      authorizedSubcategories: this.buildAuthorizedSubcategories(nav.subcategories)
    }).pipe(
      map(({ navFeatureFlagEnabled, navScopesEnabled, authorizedSubcategories }) => {
        if (!navFeatureFlagEnabled || !navScopesEnabled || authorizedSubcategories.length === 0) {
          return null;
        }
        nav.subcategories = authorizedSubcategories;
        return nav;
      })
    );
  }

  private buildAuthorizedSubcategories(subcategories: AuthISubcategory[]): Observable<ISubCategory[]> {
    return forkJoin(
      subcategories.map((subcategory) => {
        return forkJoin({
          subcategoryFeatureFlagEnabled:
            subcategory.featureFlag != null
              ? this.featureFlagService.isFeatureActive(subcategory.featureFlag)
              : of(true),
          subcategoryScopesEnabled: this.verifyRequiredScopes(subcategory.scopes, subcategory.requireAllScopes)
        });
      })
    ).pipe(
      defaultIfEmpty([]),
      map((subcategoryEnablements) => {
        return subcategories.filter((_, index) => {
          return (
            subcategoryEnablements[index].subcategoryFeatureFlagEnabled &&
            subcategoryEnablements[index].subcategoryScopesEnabled
          );
        });
      })
    );
  }

  private verifyRequiredScopes(scopes?: Scope[], requireAllScopes: boolean = true): Observable<boolean> {
    if (scopes == null || scopes.length === 0) {
      return of(true);
    }

    return requireAllScopes
      ? this.ldapTokenService.hasAllScopes(scopes)
      : this.ldapTokenService.hasScopes(scopes).pipe(map((hasScopes) => hasScopes.some((scope) => scope)));
  }
}
